import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import LogoLink from './LogoLink.jsx';
import { isMember } from './membership.js';
import { isSigningOut, signOut } from './signOut.js';

const PaymentSuccess = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Verifying your subscription...');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 8; // 8 attempts over ~16 seconds

  useEffect(() => {
    const checkSubscription = async () => {
      // Back from Stripe on a full page load: wait for the stored session.
      if (auth.isLoading) return;

      if (!auth.isAuthenticated) {
        if (isSigningOut()) return; // never race Cognito's /logout
        setStatus('Signing you in...');
        await auth.signinRedirect();
        return;
      }

      try {
        // The webhook sets custom:isSubscribed; a refreshed ID token carries it.
        setStatus(`Activating your subscription... ${retryCount > 0 ? `(${retryCount + 1}/${MAX_RETRIES})` : ''}`);
        const refreshedUser = await auth.signinSilent();

        if (isMember(refreshedUser)) {
          setStatus('Success! Redirecting to members area...');
          setTimeout(() => {
            navigate('/members');
          }, 1000);
        } else if (retryCount < MAX_RETRIES - 1) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        } else {
          setError(
            'Your payment was successful! However, account activation is taking longer than expected. ' +
            'This sometimes happens due to a slight delay. Please try one of the options below:'
          );
        }
      } catch (err) {
        console.error('Subscription activation check failed:', err.message);

        if (retryCount < MAX_RETRIES - 1) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        } else {
          setError(
            'Your subscription is active but there was an issue refreshing your account. ' +
            'Please sign out and sign back in to access the members area.'
          );
        }
      }
    };

    checkSubscription();
  }, [retryCount, auth.isLoading]); // Retry on retryCount; start once the session has loaded

  const handleRefresh = () => {
    window.location.reload();
  };

  // Same Sign Out as the dashboard: revoke, clear the stored session, end
  // the Cognito session (signOut.js).
  const handleSignOut = () => signOut(auth);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #5850c8 0%, #6b3f95 100%)',   // darkened: white text >= 6:1 (AA)
      color: '#ffffff',
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        <LogoLink className="logo-link--card logo-link--on-brand" />
        {!error ? (
          <>
            <div style={{
              fontSize: '4rem',
              marginBottom: '20px'
            }}>
              ✓
            </div>
            <h1 style={{ marginBottom: '16px', fontSize: '2rem' }}>Payment Successful!</h1>
            <p style={{ fontSize: '1.1rem', opacity: 0.9, lineHeight: '1.6' }}>
              {status}
            </p>
            {retryCount > 0 && (
              <p style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '10px' }}>
                Attempt {retryCount + 1} of {MAX_RETRIES}
              </p>
            )}
            <div style={{
              marginTop: '30px',
              display: 'inline-block'
            }}>
              <div className="loading-spinner" style={{
                border: '3px solid rgba(255, 255, 255, 0.3)',
                borderTop: '3px solid #ffffff',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontSize: '4rem',
              marginBottom: '20px'
            }}>
              ⚠️
            </div>
            <h1 style={{ marginBottom: '16px', fontSize: '2rem' }}>Almost There!</h1>
            <p style={{ 
              fontSize: '1rem', 
              opacity: 0.9, 
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              {error}
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleRefresh}
                style={{
                  background: '#ffffff',
                  color: '#667eea',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Refresh Page
              </button>
              <button
                onClick={handleSignOut}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Sign Out & Try Again
              </button>
            </div>
            <p style={{
              marginTop: '20px',
              fontSize: '0.9rem',
              opacity: 0.7
            }}>
              Need help? Email ryan@tickrtrends.com
            </p>
          </>
        )}
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;